import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  AcuityLevel,
  AcuityLevelChange,
  AcuityManualTriggers,
  AcuityTriggersConfig,
  ApcmTransitionReviewRequest,
  ApcmTransitionReviewResponse,
  AssessmentHistoryResponse,
  CreateUtilizationAssessmentRequest,
  DeescalationReviewRequest,
  DeescalationReviewResponse,
  EscalationReviewRequest,
  EscalationReviewResponse,
  ManualAcuityUpdateResponse,
  SaveManualAcuityRequest,
  UtilizationAssessment,
} from 'src/app/models/acuity.model';
import { ApiResponse } from 'src/app/models/api-response.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AcuityService {
  private baseUrl = `${environment.welbyEndpoint}/api/v1/core/acuity`;
  private acuityTriggersAssessmentSaved$ = new Subject<UtilizationAssessment>();
  private acuityLevelChanged$ = new Subject<AcuityLevelChange>();

  acuityTriggersAssessmentSaved: Observable<UtilizationAssessment> = this.acuityTriggersAssessmentSaved$.asObservable();
  acuityLevelChanged: Observable<AcuityLevelChange> = this.acuityLevelChanged$.asObservable();

  constructor(private http: HttpClient) {}

  getManualTriggers(): Observable<AcuityManualTriggers> {
    return this.http
      .get<ApiResponse<AcuityManualTriggers>>(`${this.baseUrl}/manual-triggers`)
      .pipe(map((res) => res.data));
  }

  saveManualAcuityRequest(
    patientId: string,
    dto: SaveManualAcuityRequest
  ): Observable<ManualAcuityUpdateResponse> {
    return this.http
      .post<
        ApiResponse<ManualAcuityUpdateResponse>
      >(`${this.baseUrl}/patients/${patientId}/acuity-manual-triggers`, dto)
      .pipe(
        map((res) => res.data),
        tap((response) => {
          if (response.applied && response.newAcuityLevel) {
            this.acuityLevelChanged$.next({ patientId, level: response.newAcuityLevel });
          }
        })
      );
  }

  getTriggersConfig(): Observable<AcuityTriggersConfig> {
    return this.http
      .get<ApiResponse<AcuityTriggersConfig>>(`${this.baseUrl}/triggers-config`)
      .pipe(map((res) => res.data));
  }

  saveAcuityTriggersAssessment(
    patientId: string,
    dto: CreateUtilizationAssessmentRequest
  ): Observable<UtilizationAssessment> {
    return this.http
      .post<
        ApiResponse<UtilizationAssessment>
      >(`${this.baseUrl}/patients/${patientId}/acuity-triggers-assessments`, dto)
      .pipe(
        map((res) => res.data),
        // Stamped from the argument rather than trusted from the body. The endpoint does return
        // `patientId` on both of its paths (welby-micro-service `acuity.service.ts`, the direct
        // apply and the escalation-review branch), but subscribers now filter on this field, and a
        // response that ever stopped carrying it would drop every event with nothing to show for
        // it. We already know whose assessment this is — there is no reason to ask the server.
        tap((assessment) => this.acuityTriggersAssessmentSaved$.next({ ...assessment, patientId }))
      );
  }

  getLatestAcuityTriggersAssessment(patientId: string): Observable<UtilizationAssessment | null> {
    return this.http
      .get<
        ApiResponse<UtilizationAssessment | null>
      >(`${this.baseUrl}/patients/${patientId}/acuity-triggers-assessments/latest`)
      .pipe(map((res) => res.data));
  }

  getAcuityTriggersAssessmentHistory(
    patientId: string,
    page: number,
    pageSize: number
  ): Observable<AssessmentHistoryResponse> {
    return this.http
      .get<ApiResponse<AssessmentHistoryResponse>>(
        `${this.baseUrl}/patients/${patientId}/acuity-triggers-assessments/history`,
        {
          params: { page: page.toString(), pageSize: pageSize.toString() },
        }
      )
      .pipe(map((res) => res.data));
  }

  resolveDeescalationReview(patientId: string, dto: DeescalationReviewRequest): Observable<DeescalationReviewResponse> {
    return this.http
      .post<ApiResponse<DeescalationReviewResponse>>(`${this.baseUrl}/patients/${patientId}/deescalation-review`, dto)
      .pipe(
        map((res) => res.data),
        tap((response) => {
          if (response.newAcuityLevel) {
            this.acuityLevelChanged$.next({ patientId, level: response.newAcuityLevel });
          }
        })
      );
  }

  resolveApcmTransitionReview(
    patientId: string,
    dto: ApcmTransitionReviewRequest
  ): Observable<ApcmTransitionReviewResponse> {
    return this.http
      .post<
        ApiResponse<ApcmTransitionReviewResponse>
      >(`${this.baseUrl}/patients/${patientId}/apcm-transition-review`, dto)
      .pipe(map((res) => res.data));
  }

  resolveEscalationReview(patientId: string, dto: EscalationReviewRequest): Observable<EscalationReviewResponse> {
    return this.http
      .post<ApiResponse<EscalationReviewResponse>>(`${this.baseUrl}/patients/${patientId}/escalation-review`, dto)
      .pipe(
        map((res) => res.data),
        tap((response) => {
          if (response.newAcuityLevel) {
            this.acuityLevelChanged$.next({ patientId, level: response.newAcuityLevel });
          }
        })
      );
  }
}
